/**
 * TEAMS - whose side somebody is on.
 *
 * A kill-team and the mob it is fighting; the Guard, the cultists and the
 * civilians in between. The app never had a way to say that, so every
 * picker listed ten combatants in roster order and the map painted everyone
 * who was not you or your target the same orange.
 *
 * WHAT A TEAM IS NOT. It is not ownership, not authority, and not a rule. The
 * user rejected an ownership model outright (2026-08-17) and ruled that a
 * blast may hit an ally "since this is a matter of execution". Nothing in
 * this file gates an action: any player may still act as any combatant and
 * shoot any combatant. A team does three jobs and no more:
 *
 *   1. it SORTS the targeting order - other teams first, your own last
 *   2. it COLOURS the map tokens and the order rail's band, one livery a team
 *   3. it GROUPS the gallery under a header per team
 *
 * Modelled on control networks (`control.ts`), and for the same reasons the
 * record lives on the SHEET - `character.team` - so a side travels with the
 * characters through export, import and the library. Optional and never
 * defaulted: a character in no team carries no evidence of the feature.
 *
 * Pure, zero React dependency.
 */
import type { Combatant } from './types';
import type { Character } from '../character/types';
import type { KillTeamBlock } from '../killTeam/block';
import { canonicalJson } from '../canonicalJson';

export type TeamInfo = NonNullable<Character['team']>;

/**
 * The palette, in the order an unpainted team is handed one.
 *
 * These are the colour families the app has NOT claimed: gold is the actor,
 * auspex red the target, danger red the alarm, amber the warning, green the
 * health and ammo ramps, blue the dodge and cover overlays, purple the trait
 * badge. Steel first because it is hueless and collides with nothing at all.
 * The colours themselves are `--livery-*` in theme.css; surfaces read them by
 * name (`data-livery`) and never as hex.
 */
export const TEAM_LIVERIES = ['steel', 'cobalt', 'verdigris', 'magenta', 'violet'] as const;
export type TeamLivery = (typeof TEAM_LIVERIES)[number];

/**
 * What a team may WEAR (v8.361.0): a named livery from the palette, or any
 * colour as `#rrggbb`. The name is what the roster hands out and what the
 * five swatches offer; the hex is the editor's colour picker. Surfaces
 * never branch on which: `liveryColour` turns either into the one value
 * they paint with.
 */
export type Livery = TeamLivery | `#${string}`;

const HEX_LIVERY = /^#[0-9a-f]{6}$/i;

export function isTeamLivery(x: unknown): x is TeamLivery {
  return typeof x === 'string' && (TEAM_LIVERIES as readonly string[]).includes(x);
}

/** `#rrggbb` exactly - six hex digits, the form `<input type="color">` speaks. */
export function isHexLivery(x: unknown): x is `#${string}` {
  return typeof x === 'string' && HEX_LIVERY.test(x);
}

/** A stored livery that is neither a name nor a hex (a hand-edited import) counts as unpainted. */
function declared(livery: unknown): Livery | undefined {
  if (isTeamLivery(livery)) return livery;
  if (isHexLivery(livery)) return livery.toLowerCase() as `#${string}`;
  return undefined;
}

/**
 * The CSS colour a livery paints with: a theme token for a name, the hex
 * itself for a custom colour, the hueless token for none. Every surface -
 * token body, rail band, gallery edge and header, the editor's swatches -
 * sets this into `--livery` and reads nothing else.
 */
export function liveryColour(livery: Livery | null | undefined): string {
  if (isTeamLivery(livery)) return `var(--livery-${livery})`;
  if (isHexLivery(livery)) return livery.toLowerCase();
  return 'var(--livery-none)';
}

/**
 * Anyone who can wear a team: a combatant on the roster, or a sheet in the GM's library (the Teams view, 2026-10-27).
 * The grouping below reads nothing else, so it serves both.
 */
export interface Wearer {
  id: string;
  sheet?: { team?: TeamInfo } | null;
}

export interface Team<M extends Wearer = Combatant> {
  tag: string;
  label: string;
  /** The livery the team declared, if any. `liveryFor` resolves the rest. */
  livery?: Livery;
  /** The side it fights on, as its first member declares it (`sideOf`): its own tag unless it names another. */
  side: string;
  /** Everyone carrying the tag, in roster order. */
  members: M[];
  /** The kill-team it plays as, as its first member to carry one declares it; absent for a plain team. */
  killTeam?: KillTeamBlock;
}

/**
 * This combatant's team record, or null.
 *
 * A blank tag is NOT a team. A half-filled Team tab - a title typed, no tag
 * minted - is the likeliest state this feature will ever be in, and treating
 * it as a team would silently group every such character together.
 */
export function teamOf(combatant: Wearer | null | undefined): TeamInfo | null {
  const team = combatant?.sheet?.team;
  if (!team) return null;
  return team.tag && team.tag.trim() ? team : null;
}

/**
 * Everyone carrying a tag.
 *
 * An absent tag matches NOBODY: `team?.tag === tag` with both sides undefined
 * is true for every ordinary character, which would put the whole roster on
 * one side.
 */
export function teamMembers<M extends Wearer>(roster: readonly M[], tag: string | null | undefined): M[] {
  if (!tag || !tag.trim()) return [];
  return roster.filter((c) => teamOf(c)?.tag === tag);
}

/**
 * Do these two fight on the same side?
 *
 * False when either has no team: two bystanders are not a team, and a
 * combatant is not on their own side for the purpose of "own team last".
 */
export function sameTeam(a: Combatant | null | undefined, b: Combatant | null | undefined): boolean {
  const ta = teamOf(a)?.tag;
  const tb = teamOf(b)?.tag;
  return !!ta && !!tb && ta === tb;
}

/**
 * The side a team record fights on: the side it names, else its own tag; null for no team (2026-10-23). Two teams
 * on one side are ALLIES and stay two teams - their own titles, colours and members. A side is never a gate either:
 * it widens what "allies" means for auras and exposures, nothing more.
 */
export function sideOf(team: TeamInfo | null | undefined): string | null {
  const tag = team?.tag?.trim();
  if (!tag) return null;
  return team?.side?.trim() || tag;
}

/** The SIDE a stored key names (a party picked by the GM): a team's tag reads as that team's side; any other key is
 * a side as it stands. So a key saved before a team named another side still finds its allies. */
export function sideKeyIn(teams: readonly Pick<Team, 'tag' | 'side'>[], key: string | null | undefined): string | null {
  const k = key?.trim();
  if (!k) return null;
  return teams.find((t) => t.tag === k)?.side ?? k;
}

/** A team as a picker offers it: who it is, and how many sheets wear it. */
export interface TeamChoice {
  tag: string;
  label: string;
  livery?: Livery;
  side: string;
  count: number;
  /** The kill-team block a sheet joining it takes (its first wearer's that carries one); never the leadership. */
  killTeam?: KillTeamBlock;
  /** Who leads its kill-team, by name, when the list knows (the library's does); absent for nobody or unknown. */
  leader?: string;
}

/**
 * The teams a list of sheets wears, once each in order of first appearance, as their first record declares them (the
 * first declared livery speaks for the team, as in `teamsIn`): the Team tab's "Join an existing team", fed by the
 * roster and the library in a session, by the library on the Assets page.
 */
export function teamChoices(records: readonly (TeamInfo | null | undefined)[]): TeamChoice[] {
  const byTag = new Map<string, TeamChoice>();
  for (const info of records) if (info) addChoice(byTag, info);
  return [...byTag.values()];
}

/** The teams a list of named sheets wears, each choice naming who leads its kill-team (the first sheet that does). */
export function choicesWithLeaders(sheets: readonly { name: string; team?: TeamInfo | null }[]): TeamChoice[] {
  return teamChoices(sheets.map((s) => s.team)).map((choice) => {
    const leader = sheets.find((s) => s.team?.tag === choice.tag && s.team.leads === true);
    return leader ? { ...choice, leader: leader.name } : choice;
  });
}

/** One sheet's record: a new choice, or one more wearer of a known one (its first declared livery and block kept). */
function addChoice(byTag: Map<string, TeamChoice>, info: TeamInfo): void {
  const tag = info.tag?.trim();
  if (!tag) return;
  let choice = byTag.get(tag);
  if (!choice) {
    choice = { tag, label: info.label || tag, side: sideOf(info)!, count: 0 };
    byTag.set(tag, choice);
  }
  wearAlso(choice, info);
}

/** One more wearer of a choice: counted, and what the choice still lacks taken from him. */
function wearAlso(choice: TeamChoice, info: TeamInfo): void {
  choice.count++;
  const worn = declared(info.livery);
  if (!choice.livery && worn) choice.livery = worn;
  if (!choice.killTeam && info.killTeam) choice.killTeam = info.killTeam;
}

/** A team as a side lists it. */
type OnASide = Pick<Team, 'tag' | 'label' | 'side'>;

/** One side and the teams on it, in order of first appearance - what a picker of sides lists. */
export interface Side<T extends OnASide = Team> {
  side: string;
  teams: T[];
}

/** The teams grouped by their side, sides in order of their first team. */
export function sidesOfTeams<T extends OnASide>(teams: readonly T[]): Side<T>[] {
  const out: Side<T>[] = [];
  for (const team of teams) {
    // A team record built by hand (a test, an old caller) may carry no side: its own tag, as `sideOf` reads it.
    const side = team.side || team.tag;
    const found = out.find((s) => s.side === side);
    if (found) found.teams.push(team);
    else out.push({ side, teams: [team] });
  }
  return out;
}

/** Is this combatant on that side? Never for no side, never for the teamless. */
export const onSide = (c: Combatant | null | undefined, side: string | null | undefined): boolean =>
  !!side && sideOf(teamOf(c)) === side;

/** Do these two fight on the same side? False when either has no team - a bystander is nobody's ally. */
export function sameSide(a: Combatant | null | undefined, b: Combatant | null | undefined): boolean {
  const sa = sideOf(teamOf(a));
  return !!sa && sa === sideOf(teamOf(b));
}

/** On opposing sides: both on a side, and not the same one. Teamless is nobody's foe. The cue's and melee's rule. */
export function opposedSides(a: Combatant | null | undefined, b: Combatant | null | undefined): boolean {
  const sa = sideOf(teamOf(a));
  const sb = sideOf(teamOf(b));
  return !!sa && !!sb && sa !== sb;
}

/**
 * Every team on the roster, once each, in order of first appearance.
 *
 * A team of one COUNTS, unlike a control network of one: the opposition may
 * be a single Carnifex, and it still needs a side and a colour.
 */
export function teamsIn<M extends Wearer>(roster: readonly M[]): Team<M>[] {
  const byTag = new Map<string, Team<M>>();
  for (const c of roster) {
    const info = teamOf(c);
    if (!info?.tag) continue;
    const existing = byTag.get(info.tag);
    if (existing) joinTeam(existing, c, declared(info.livery));
    else byTag.set(info.tag, newTeam(info.tag, info.label, declared(info.livery), c));
  }
  return [...byTag.values()];
}

/** A team, as its first member declares it. */
function newTeam<M extends Wearer>(
  tag: string,
  label: string | undefined,
  worn: Livery | undefined,
  first: M,
): Team<M> {
  const side = sideOf(teamOf(first)) ?? tag;
  const block = teamOf(first)?.killTeam;
  return {
    tag,
    label: label || tag,
    ...(worn ? { livery: worn } : {}),
    side,
    members: [first],
    ...(block ? { killTeam: block } : {}),
  };
}

/** Add a member; the first member to declare a livery, and the first to carry a kill-team, speak for the team. */
function joinTeam<M extends Wearer>(team: Team<M>, c: M, worn: Livery | undefined): void {
  team.members.push(c);
  if (!team.livery && worn) team.livery = worn;
  const block = teamOf(c)?.killTeam;
  if (!team.killTeam && block) team.killTeam = block;
}

/** One kill-team block among a team's wearers, and who carries it (none: the plain-team record). */
export interface BlockGroup<M extends Wearer> {
  block: KillTeamBlock | null;
  members: M[];
}

/**
 * A team's wearers by the kill-team block they carry, the largest group first (plan modes-rework). More than one group
 * is a team whose sheets disagree - a sheet saved before the block changed, a scene's copy left behind - and it is never
 * resolved by guessing which is newer: the Teams view lists the groups and the GM picks one.
 */
export function blockGroups<M extends Wearer>(team: Team<M>): BlockGroup<M>[] {
  const byKey = new Map<string, BlockGroup<M>>();
  for (const m of team.members) {
    const block = teamOf(m)?.killTeam ?? null;
    const key = canonicalJson(block);
    const group = byKey.get(key);
    if (group) group.members.push(m);
    else byKey.set(key, { block, members: [m] });
  }
  return [...byKey.values()].sort((a, b) => b.members.length - a.members.length);
}

/**
 * The livery a team wears: the one it declared, else the first in the
 * palette that no earlier team on the roster wears.
 *
 * "Earlier" is roster order, so the answer is stable while the roster stands
 * and two unpainted teams never share while the palette lasts. Past five
 * teams it wraps, which is a collision rather than a failure - the editor's
 * swatches are how a table with six factions tells them apart.
 */
export function liveryFor<M extends Wearer>(team: Team<M>, roster: readonly M[]): Livery {
  if (team.livery) return team.livery;
  const teams = teamsIn(roster);
  const taken = new Set(teams.filter((t) => t.tag !== team.tag && t.livery).map((t) => t.livery));
  const unpainted = teams.filter((t) => !t.livery);
  const free = TEAM_LIVERIES.filter((l) => !taken.has(l));
  const rank = unpainted.findIndex((t) => t.tag === team.tag);
  // A team not on this roster at all (rank -1) is treated as the first
  // unpainted one, which is the only sensible answer for a preview.
  const slot = Math.max(rank, 0);
  return free.length ? free[slot % free.length] : TEAM_LIVERIES[slot % TEAM_LIVERIES.length];
}

/**
 * A sheet's record as the teams read it: the title falls back to the tag, a livery that is neither a name nor a hex is
 * none, a hex is lower-case, and a side naming the team's own tag is no side. Two records that normalise alike mean
 * the same team on the map and in every list.
 */
export function normalTeamInfo(info: TeamInfo): TeamInfo {
  const livery = declared(info.livery);
  const record = teamInfoOf({
    tag: info.tag ?? '',
    label: info.label || info.tag || '',
    ...(livery ? { livery } : {}),
    side: info.side,
    killTeam: info.killTeam,
  });
  return asMember(record, info);
}

/**
 * A team record as one member wears it: the record, with what is that member's own put back - his leadership. The one
 * place member-only fields return, so a verb that rebuilds a record never drops or invents a leader by accident.
 */
export function asMember(record: TeamInfo, own: TeamInfo | null | undefined): TeamInfo {
  const team = withoutLead(record);
  return own?.leads === true ? { ...team, leads: true } : team;
}

/** A record with nobody's leadership on it. */
export const withoutLead = ({ leads: _was, ...record }: TeamInfo): TeamInfo => record;

/** A record of a plain team: no kill-team block on it (the leadership is the caller's to keep or drop). */
export const withoutBlock = ({ killTeam: _was, ...record }: TeamInfo): TeamInfo => record;

/** One team, one leader: the first leader among `preferred` (a kept group), else among everyone; null for none. */
export function soleLeader(preferred: readonly Wearer[], everyone: readonly Wearer[]): string | null {
  const leads = (m: Wearer) => teamOf(m)?.leads === true;
  return (preferred.find(leads) ?? everyone.find(leads))?.id ?? null;
}

/**
 * The block a team plays: its leader's, else the one most of its sheets carry, a tie settled by the block itself -
 * never by the members' order (a reorder must never switch the Oath, which resets the pool). Spread keeps the sheets
 * agreeing; this only decides while they do not. Undefined for a plain team.
 */
export function playedBlock<M extends Wearer>(team: Team<M>, leader: M | null): KillTeamBlock | undefined {
  const own = leader ? teamOf(leader)?.killTeam : undefined;
  if (own) return own;
  const groups = blockGroups(team).filter((g) => g.block);
  const most = groups.filter((g) => g.members.length === groups[0]?.members.length).map((g) => g.block!);
  return most.sort((a, b) => canonicalJson(a).localeCompare(canonicalJson(b)))[0];
}

/**
 * The record a member of this team carries on their sheet: the side only when it names another team, the kill-team
 * block as a sheet reads it. Never the leadership - joining a team never makes anyone its leader.
 */
export function teamInfoOf(
  team: Pick<Team, 'tag' | 'label' | 'livery'> & { side?: string; killTeam?: KillTeamBlock },
): TeamInfo {
  const side = team.side?.trim();
  return {
    tag: team.tag,
    label: team.label,
    ...(team.livery ? { livery: team.livery } : {}),
    ...(side && side !== team.tag ? { side } : {}),
    ...(team.killTeam ? { killTeam: team.killTeam } : {}),
  };
}

/** One title and colour among the wearers of a tag. */
interface TagGroup<M extends Wearer> {
  label: string;
  livery?: Livery;
  members: M[];
}

/** A tag worn by more than one title and colour (the M6 case: two teams typed the same tag), the largest first. */
export interface TagConflict<M extends Wearer> {
  tag: string;
  groups: TagGroup<M>[];
}

/** Put a wearer in the group of its title and colour (any colour, for one that declared none), or start one. */
function joinGroup<M extends Wearer>(groups: TagGroup<M>[], c: M, label: string, livery: Livery | undefined): void {
  const g = groups.find((x) => x.label === label && (!livery || x.livery === livery));
  if (g) g.members.push(c);
  else groups.push({ label, ...(livery ? { livery } : {}), members: [c] });
}

/**
 * A tag's wearers by title, then colour, the largest group first. The painted are placed first, so a wearer who
 * declared no colour joins the first group of its title rather than starting one of its own.
 */
function groupsOfTag<M extends Wearer>(team: Team<M>): TagGroup<M>[] {
  const groups: TagGroup<M>[] = [];
  const painted = (c: M) => !!declared(teamOf(c)!.livery);
  const ordered = [...team.members.filter(painted), ...team.members.filter((c) => !painted(c))];
  for (const c of ordered) joinGroup(groups, c, teamOf(c)!.label || team.tag, declared(teamOf(c)!.livery));
  return groups.sort((a, b) => b.members.length - a.members.length);
}

/** A conflict's groups in words, largest first: "Servidores (2), Kill-team Omega (1)". */
export const groupsLine = (groups: readonly { label: string; members: readonly unknown[] }[]): string =>
  groups.map((g) => `${g.label} (${g.members.length})`).join(', ');

/**
 * Every tag the roster wears under more than one title and colour. Never resolved silently: the Teams dialog says
 * so and offers the split (ruled 2026-10-23), because the tag alone is what `teamsIn` matches on.
 */
export function tagConflicts<M extends Wearer>(roster: readonly M[]): TagConflict<M>[] {
  return teamsIn(roster)
    .map((team) => ({ tag: team.tag, groups: groupsOfTag(team) }))
    .filter((c) => c.groups.length > 1);
}

/**
 * The split of a conflicting tag: the largest group keeps it; every other group gets a fresh tag (`mint(i)`, the
 * uniqueness from outside - gotcha 5) and the old tag's SIDE, so the halves stay allies. Each entry is a member's new
 * team record; empty when the tag is not in conflict.
 */
export function splitPlan(
  roster: readonly Wearer[],
  tag: string,
  mint: (i: number) => string,
): { id: string; team: TeamInfo }[] {
  const conflict = tagConflicts(roster).find((c) => c.tag === tag);
  if (!conflict) return [];
  // The side the tag stands on is the largest group's: the halves stay where the team stood.
  const side = sideOf(teamOf(conflict.groups[0].members[0]))!;
  return conflict.groups.slice(1).flatMap((g, i) =>
    g.members.map((m) => ({
      id: m.id,
      // A kill-team block stays with the tag that keeps it: a split-off group is a plain team, led by nobody.
      team: { tag: mint(i), label: g.label, ...(g.livery ? { livery: g.livery } : {}), side },
    })),
  );
}

/** A member's record on a new side (`''`: its own), its title and colour untouched. */
function onNewSide(info: TeamInfo, side: string): TeamInfo {
  const { side: _was, ...rest } = info;
  return side && side !== info.tag ? { ...rest, side } : rest;
}

/**
 * Every sheet's record when the team `tag` takes the side `side` (`''`: a side of its own). Each member keeps its own
 * title and colour - a side is never a reason to flatten a merged tag. An ANCHOR team (one whose side is its own tag)
 * moves its whole side with it, so a team set beside it is never left on a side nobody anchors.
 */
export function sidePlan(roster: readonly Wearer[], tag: string, side: string): { id: string; team: TeamInfo }[] {
  const team = teamsIn(roster).find((t) => t.tag === tag);
  if (!team) return [];
  // An anchor going to another side carries its side; an anchor "going" to its own side changes nobody else.
  const carries = team.side === team.tag && !!side && side !== tag;
  const moving = (c: Wearer) => {
    const info = teamOf(c);
    return !!info && (info.tag === tag || (carries && sideOf(info) === tag));
  };
  return roster.filter(moving).map((c) => ({ id: c.id, team: onNewSide(teamOf(c)!, side) }));
}

/**
 * A sheet moved to a team, or to none. A new object: a sheet is never
 * edited in place. "No team" removes the record rather than blanking it,
 * so a character in no team carries no evidence of the feature.
 */
export function sheetOnTeam<T extends { team?: TeamInfo }>(sheet: T, team: TeamInfo | null): T {
  if (team) return { ...sheet, team };
  const { team: _left, ...rest } = sheet;
  return rest as T;
}

/**
 * Mint a tag from a unique part supplied by the caller.
 *
 * The uniqueness comes from OUTSIDE - engine code does not reach for
 * randomness (gotcha #5).
 */
export function teamTag(unique: string): string | null {
  const trimmed = (unique || '').trim();
  return trimmed ? `team-${trimmed}` : null;
}

/**
 * The livery every combatant wears, by id - the map's question, asked once
 * per roster change rather than once per token per frame. A combatant in
 * no team is absent from the record, and the token draws hueless.
 */
export function liveryByCombatant(roster: readonly Combatant[]): Record<string, Livery> {
  const out: Record<string, Livery> = {};
  for (const team of teamsIn(roster)) {
    const livery = liveryFor(team, roster);
    for (const member of team.members) out[member.id] = livery;
  }
  return out;
}

export interface TeamGroup {
  /** The team, or null for the combatants in none. */
  team: Team | null;
  livery: Livery | null;
  members: Combatant[];
}

/**
 * The roster in the order a picker should offer it: OTHER teams first, in
 * order of first appearance, then the teamless, and the group holding the
 * actor LAST - your own side is the one you shoot at least.
 *
 * A sort and nothing more: every combatant is still offered. The teamless
 * group exists only when there is at least one team, since a roster with no
 * teams at all has nothing to group by and the header would be noise.
 */
export function groupByTeam(roster: readonly Combatant[], actorId: string | null | undefined): TeamGroup[] {
  const teams = teamsIn(roster);
  if (teams.length === 0) return [{ team: null, livery: null, members: [...roster] }];
  const groups: TeamGroup[] = teams.map((team) => ({ team, livery: liveryFor(team, roster), members: team.members }));
  const teamless = roster.filter((c) => !teamOf(c));
  if (teamless.length) groups.push({ team: null, livery: null, members: teamless });
  const actor = roster.find((c) => c.id === actorId);
  if (!actor) return groups;
  const own = groups.findIndex((g) => g.members.some((m) => m.id === actor.id));
  if (own < 0) return groups;
  const [mine] = groups.splice(own, 1);
  groups.push(mine);
  return groups;
}
